import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import { ELEVATION } from '@openx/components-core/lib/theme/elevation.ts';
import { palette } from '@openx/components-core/lib/theme/paletteV2.ts';
import { type ReactNode, useEffect, useRef, useState } from 'react';

const HIDE_DELAY = 300;

const scrollButtonStyle = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 10,
  backgroundColor: palette.background.boxDarker,
  boxShadow: ELEVATION.elevation8,

  '&:hover': {
    backgroundColor: palette.background.boxDarkest,
  },
};

type FiltersBarScrollableWrapperProps = {
  children: ReactNode;
  forceVisible?: boolean;
};

export const FiltersBarScrollableWrapper = ({ children, forceVisible }: FiltersBarScrollableWrapperProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const [visible, setVisible] = useState(false);

  const updateScrollButtons = () => {
    const el = scrollRef.current;
    const tolerance = 2;

    if (!el) return;

    setShowLeft(el.scrollLeft > 0);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - tolerance);
  };

  const scrollByAmount = (amount: number) => {
    scrollRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    setVisible(true);
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      if (!forceVisible) {
        setVisible(false);
      }
    }, HIDE_DELAY);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: It should be set only once on initial render
  useEffect(() => {
    const el = scrollRef.current;
    const contentEl = contentRef.current;
    if (!el || !contentEl) return;

    updateScrollButtons();

    const handleResize = () => updateScrollButtons();
    el.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', handleResize);

    const observer = new ResizeObserver(updateScrollButtons);
    observer.observe(el);
    observer.observe(contentEl);

    return () => {
      el.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, []);

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-test="filters-bar-scrollable-wrapper"
      sx={{
        position: 'relative',
        marginLeft: 1,
      }}
    >
      <Fade in={(visible && showLeft) || (showLeft && forceVisible)} timeout={200} unmountOnExit>
        <IconButton
          data-test="scroll-left-button"
          aria-label="scroll left"
          onClick={() => scrollByAmount(-200)}
          sx={{
            ...scrollButtonStyle,
            left: 0,
          }}
        >
          <ChevronLeft />
        </IconButton>
      </Fade>

      <Fade in={(visible && showRight) || (showRight && forceVisible)} timeout={200} unmountOnExit>
        <IconButton
          data-test="scroll-right-button"
          aria-label="scroll right"
          onClick={() => scrollByAmount(200)}
          sx={{
            ...scrollButtonStyle,
            right: 0,
          }}
        >
          <ChevronRight />
        </IconButton>
      </Fade>

      <Box
        ref={scrollRef}
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          width: '100%',
          scrollBehavior: 'smooth',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Box
          ref={contentRef}
          sx={{
            display: 'inline-flex',
            width: 'max-content',
            alignItems: 'center',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};
